// Modern UI Implementation for NeuroPsyMap
// This module provides a modernized UI interface that can be integrated into the existing UI.ts

import { NeuralNetworkSimulator } from '../NeuralNetworkSimulator';
import {
  createButton, createCard, createCollapsibleSection,
  createStatItem, createLargeStat, createProgressBar,
  createHeader, createInfoBar, createInfoBarText,
  createBreathingVisual, createVoiceVisual,
  createTranscendenceVisual,
  ButtonConfig,
  LogPanel
} from './components';

export class ModernUI {
  private network: NeuralNetworkSimulator;
  private sidebar: HTMLElement;
  private headerElement: HTMLElement;
  private infoBarElement: HTMLElement;
  private sections: Map<string, HTMLElement> = new Map();
  private logPanel: LogPanel;
  
  constructor(network: NeuralNetworkSimulator) {
    this.network = network;
    this.initializeStructure();
    // Initialize log panel
    this.logPanel = new LogPanel();
  }
  
  private sidebarVisible: boolean = false; // Default to collapsed
  private toggleBtn: HTMLButtonElement | null = null;
  
  private initializeStructure() {
    // Create header
    this.headerElement = this.createModernHeader();
    document.body.appendChild(this.headerElement);
    
    // Create info bar
    this.infoBarElement = this.createInfoBar();
    document.body.appendChild(this.infoBarElement);
    
    // Create sidebar as overlay with toggle button
    this.sidebar = document.createElement('div');
    this.sidebar.className = 'sidebar scrollbar-thin';
    this.sidebar.style.transition = 'transform 0.3s ease';
    
    // Start collapsed
    this.sidebar.style.transform = 'translateX(-100%)';
    
    // Create toggle button (before appending sidebar)
    this.createSidebarToggle();
    
    document.body.appendChild(this.sidebar);
    
    // Populate sidebar with modern sections
    this.updateSidebar();
  }
  
  private createSidebarToggle() {
    this.toggleBtn = document.createElement('button');
    this.toggleBtn.innerHTML = '☰';
    this.toggleBtn.className = 'btn btn-ghost';
    this.toggleBtn.style.position = 'fixed';
    this.toggleBtn.style.left = '8px';
    this.toggleBtn.style.top = '80px';
    this.toggleBtn.style.zIndex = '10001';
    this.toggleBtn.style.width = '44px';
    this.toggleBtn.style.height = '44px';
    this.toggleBtn.style.borderRadius = 'var(--radius-md)';
    this.toggleBtn.style.fontSize = '20px';
    this.toggleBtn.style.padding = '0';
    this.toggleBtn.style.display = 'flex';
    this.toggleBtn.style.alignItems = 'center';
    this.toggleBtn.style.justifyContent = 'center';
    this.toggleBtn.style.background = 'rgba(31, 41, 55, 0.8)';
    this.toggleBtn.style.backdropFilter = 'blur(8px)';
    this.toggleBtn.style.border = '1px solid var(--color-bg-tertiary)';
    this.toggleBtn.style.boxShadow = 'var(--shadow-lg)';
    this.toggleBtn.onclick = () => {
      this.sidebarVisible = !this.sidebarVisible;
      if (this.sidebarVisible) {
        this.sidebar.style.transform = 'translateX(0)';
        this.toggleBtn!.innerHTML = '☰';
        this.toggleBtn!.style.left = '300px';
      } else {
        this.sidebar.style.transform = 'translateX(-100%)';
        this.toggleBtn!.innerHTML = '☰';
        this.toggleBtn!.style.left = '8px';
      }
    };
    document.body.appendChild(this.toggleBtn);
  }
  
  private createModernHeader(): HTMLElement {
    const header = createHeader('🧠 Neural Network Simulator', [
      createButton({
        label: 'Restart',
        variant: 'primary',
        size: 'small',
        icon: '🔄',
        onClick: () => window.location.reload()
      }),
      createButton({
        label: 'Export',
        variant: 'success',
        size: 'small',
        icon: '📥',
        onClick: () => this.exportData()
      }),
      createButton({
        label: 'Load',
        variant: 'success',
        size: 'small',
        icon: '📤',
        onClick: () => this.loadData()
      }),
      createButton({
        label: 'Sun',
        variant: 'warning',
        size: 'small',
        icon: '☀️',
        onClick: () => this.toggleSun()
      }),
      createButton({
        label: 'Logs',
        variant: 'ghost',
        size: 'small',
        icon: '📋',
        onClick: () => this.toggleLogPanel()
      })
    ]);
    
    return header;
  }
  
  private toggleLogPanel() {
    const logPanel = document.querySelector('.log-panel') as HTMLElement;
    if (logPanel) {
      if (logPanel.style.display === 'none') {
        this.logPanel.show();
      } else {
        this.logPanel.hide();
      }
    }
  }
  
  private createInfoBar(): HTMLElement {
    return createInfoBar([
      createInfoBarText('Nodes: <strong>0</strong>'),
      createInfoBarText('Edges: <strong>0</strong>'),
      createInfoBarText('State: <strong>Growing</strong>'),
      createInfoBarText('Energy: <strong>0</strong>')
    ]);
  }
  
  private updateSidebar() {
    const sections: HTMLElement[] = [];
    
    // Quick Stats Section (always visible)
    const quickStats = this.createQuickStatsSection();
    sections.push(quickStats);
    this.sections.set('quick-stats', quickStats);
    
    // Breathing System Section
    const breathingSection = this.createBreathingSection();
    sections.push(breathingSection);
    this.sections.set('breathing', breathingSection);
    
    // Voice System Section
    const voiceSection = this.createVoiceSection();
    sections.push(voiceSection);
    this.sections.set('voice', voiceSection);

    // Mouth (Articulators) Section
    const mouthSection = this.createMouthSection();
    sections.push(mouthSection);
    this.sections.set('mouth', mouthSection);
    
    // Evolution & Transcendence Section
    const evolutionSection = this.createEvolutionSection();
    sections.push(evolutionSection);
    this.sections.set('evolution', evolutionSection);
    
    // Advanced Stats Section (collapsible)
    const advancedSection = this.createAdvancedStatsSection();
    sections.push(advancedSection);
    this.sections.set('advanced', advancedSection);
    
    // Update sidebar content
    this.sidebar.innerHTML = '';
    sections.forEach(section => {
      this.sidebar.appendChild(section);
    });
  }

  private createMouthSection(): HTMLElement {
    const content = document.createElement('div');
    content.style.padding = 'var(--space-4)';

    const mouth = (this.network as any).mouthSystem;
    if (!mouth) {
      content.textContent = 'Mouth system initializing...';
      return createCard({ title: '👄 Mouth', content, collapsible: true, className: 'mb-3' });
    }

    const s = mouth.getState();
    const exploration = mouth.getExplorationState?.();

    const proceduralNote = document.createElement('div');
    proceduralNote.className = 'text-sm text-tertiary';
    proceduralNote.style.marginBottom = '10px';
    proceduralNote.textContent = 'Mouth articulation is procedurally driven by simulation growth.';
    content.appendChild(proceduralNote);

    content.appendChild(createStatItem('Jaw Open', s.jawOpen.toFixed(2)));
    content.appendChild(createStatItem('Tongue Frontness', s.tongueFrontness.toFixed(2)));
    content.appendChild(createStatItem('Tongue Height', s.tongueHeight.toFixed(2)));
    content.appendChild(createStatItem('Lip Roundness', s.lipRoundness.toFixed(2)));
    content.appendChild(createStatItem('Nasal Port', s.nasalPort.toFixed(2)));

    if (exploration) {
      content.appendChild(createStatItem('Exploration Phase', exploration.explorationPhase));
      content.appendChild(createStatItem('Curiosity', `${(exploration.curiosity * 100).toFixed(1)}%`));
      content.appendChild(createStatItem('Learned Mouth Patterns', exploration.learnedConfigs.size));
      content.appendChild(createStatItem('Phonation Success', `${exploration.successfulPhonations}/${exploration.totalPhonations}`));
    }

    const btnRow = document.createElement('div');
    btnRow.style.display = 'flex';
    btnRow.style.gap = '8px';
    btnRow.style.marginTop = '12px';

    btnRow.appendChild(createButton({
      label: 'Phonate',
      variant: 'primary',
      size: 'small',
      onClick: () => this.network.creatureVoice.phonateWithMouth(700)
    }));

    btnRow.appendChild(createButton({
      label: 'Exhale + Phonate',
      variant: 'ghost',
      size: 'small',
      onClick: () => {
        // Nudge breathing to exhale if possible, then phonate
        try {
          const b = this.network.breathingSystem.getBreathingState();
          if (b.cyclePhase === 'inhale' || b.cyclePhase === 'pause_inhale') {
            (this.network.breathingSystem as any).forceExhale?.();
          }
        } catch {}
        this.network.creatureVoice.phonateWithMouth(700);
      }
    }));

    content.appendChild(btnRow);

    const card = createCard({
      title: '👄 Mouth',
      content,
      collapsible: true,
      className: 'mb-3'
    });
    return card;
  }
  
  private createEvolutionSection(): HTMLElement {
    // Get evolution metrics if available
    const evolutionEngine = (this.network as any).limbEvolution;
    const metrics = evolutionEngine?.getEvolutionMetrics?.();
    
    if (!metrics) {
      // Return empty card if no evolution data
      const content = document.createElement('div');
      content.style.padding = 'var(--space-4)';
      content.textContent = 'Evolution system initializing...';
      
      return createCard({
        title: '🧬 Evolution',
        content: content,
        collapsible: true,
        defaultExpanded: false,
        className: 'mb-3'
      });
    }
    
    // Count emergent traits
    const emergentTraits: string[] = [];
    if (evolutionEngine?.discoveredEmergentTraits instanceof Set) {
      emergentTraits.push(...Array.from(evolutionEngine.discoveredEmergentTraits as Set<string>));
    }
    
    // Count feedback loops
    let feedbackLoopCount = 0;
    if (metrics.feedbackStrengths instanceof Map) {
      for (const [_, strength] of Array.from(metrics.feedbackStrengths as Map<string, number>)) {
        if (strength > 0.5) feedbackLoopCount++;
      }
    }
    
    const bootstrapBoost = metrics.selfEnhancementFactor || 0;
    
    const transcendenceVisual = createTranscendenceVisual(
      emergentTraits,
      feedbackLoopCount,
      bootstrapBoost,
      metrics.carryingCapacity || 0,
      metrics.currentDensity || 0
    );
    
    return createCard({
      title: '🧬 Evolution & Transcendence',
      content: transcendenceVisual,
      collapsible: true,
      defaultExpanded: false,
      className: 'mb-3'
    });
  }
  
  private createQuickStatsSection(): HTMLElement {
    const nodeCount = this.network.nodes.length;
    const edgeCount = this.network.edges.length;
    const avgEnergy = nodeCount > 0 
      ? this.network.nodes.reduce((sum, n) => sum + n.energy, 0) / nodeCount 
      : 0;
    
    const content = document.createElement('div');
    content.style.display = 'flex';
    content.style.flexDirection = 'column';
    content.style.gap = 'var(--space-3)';
    
    // Compact horizontal stats
    const statsRow = document.createElement('div');
    statsRow.style.display = 'grid';
    statsRow.style.gridTemplateColumns = '1fr 1fr';
    statsRow.style.gap = 'var(--space-2)';
    statsRow.appendChild(createLargeStat('Nodes', nodeCount));
    statsRow.appendChild(createLargeStat('Edges', edgeCount));
    content.appendChild(statsRow);
    
    const energyProgress = createProgressBar(avgEnergy * 10, 'Energy');
    content.appendChild(energyProgress);
    
    const card = createCard({
      title: '📊 Stats',
      content: content,
      className: 'mb-3'
    });
    
    return card;
  }
  
  private createBreathingSection(): HTMLElement {
    const breathingState = this.network.breathingSystem.getBreathingState();
    const breathingThresholds = this.network.breathingSystem.getVoiceCapacityThresholds();
    
    const breathingVisual = createBreathingVisual(
      breathingState.oxygenLevel,
      breathingState.carbonDioxideLevel,
      breathingState.cyclePhase
    );
    
    const content = document.createElement('div');
    
    const controlButtons = document.createElement('div');
    controlButtons.className = 'flex gap-2';
    controlButtons.style.marginTop = 'var(--space-4)';
    controlButtons.appendChild(createButton({
      label: 'Force Breath',
      variant: 'success',
      size: 'small',
      onClick: () => this.network.breathingSystem.forceBreath()
    }));
    controlButtons.appendChild(createButton({
      label: 'Sounds',
      variant: 'ghost',
      size: 'small',
      icon: '🔊',
      onClick: () => this.toggleBreathingSounds()
    }));
    
    content.appendChild(breathingVisual);
    content.appendChild(controlButtons);
    
    const card = createCard({
      title: '🫁 Breathing',
      content: content,
      collapsible: true,
      defaultExpanded: true,
      className: 'mb-3'
    });
    
    return card;
  }
  
  private createVoiceSection(): HTMLElement {
    const voiceState = this.network.creatureVoice.getCurrentVoiceState();
    
    const voiceVisual = createVoiceVisual(
      voiceState.currentEmotion,
      this.network.voiceCharacteristics.personality,
      voiceState.vocalMaturity * 100
    );
    
    const content = document.createElement('div');
    
    const controlButtons = document.createElement('div');
    controlButtons.className = 'flex gap-2';
    controlButtons.style.marginTop = 'var(--space-4)';
    controlButtons.appendChild(createButton({
      label: '← Personality',
      variant: 'ghost',
      size: 'small',
      onClick: () => this.cyclePersonality()
    }));
    controlButtons.appendChild(createButton({
      label: 'Emotion →',
      variant: 'ghost',
      size: 'small',
      onClick: () => this.cycleEmotion()
    }));
    
    content.appendChild(voiceVisual);
    content.appendChild(controlButtons);
    
    const card = createCard({
      title: '🗣️ Voice',
      content: content,
      collapsible: true,
      className: 'mb-3'
    });
    
    return card;
  }
  
  private createAdvancedStatsSection(): HTMLElement {
    const avgEnergy = this.network.nodes.length > 0 
      ? (this.network.nodes.reduce((sum, n) => sum + n.energy, 0) / this.network.nodes.length).toFixed(3)
      : '0';
    
    const avgValue = this.network.nodes.length > 0
      ? (this.network.nodes.reduce((sum, n) => sum + n.value, 0) / this.network.nodes.length).toFixed(3)
      : '0';
    
    const growthRate = (this.network.nodes.length / Math.max(1, Math.floor(this.network.step / this.network.evolutionInterval))).toFixed(2);
    
    const content = document.createElement('div');
    
    content.appendChild(createStatItem('Average Energy', avgEnergy));
    content.appendChild(createStatItem('Average Value', avgValue));
    content.appendChild(createStatItem('Growth Rate', growthRate));
    content.appendChild(createStatItem('Pulse Interval', `${this.network.pulseInterval} steps`));
    content.appendChild(createStatItem('Evolution Interval', `${this.network.evolutionInterval} steps`));
    content.appendChild(createStatItem('Current Step', this.network.step));
    
    const card = createCard({
      title: '📊 Advanced',
      content: content,
      collapsible: true,
      defaultExpanded: false,
      className: 'mb-3'
    });
    
    return card;
  }
  
  public update() {
    // Update quick stats
    const quickStats = this.sections.get('quick-stats');
    if (quickStats) {
      const newQuickStats = this.createQuickStatsSection();
      quickStats.replaceWith(newQuickStats);
      this.sections.set('quick-stats', newQuickStats);
    }

    // Update mouth section
    const mouthSection = this.sections.get('mouth');
    if (mouthSection) {
      const newMouthSection = this.createMouthSection();
      mouthSection.replaceWith(newMouthSection);
      this.sections.set('mouth', newMouthSection);
    }
    
    // Update info bar
    const nodeCount = this.network.nodes.length;
    const edgeCount = this.network.edges.length;
    const currentState = this.network.creatureState;
    const totalEnergy = this.network.totalNetworkEnergy?.toFixed(1) || '0';
    
    this.infoBarElement.innerHTML = '';
    this.infoBarElement.appendChild(createInfoBarText(`Nodes: <strong>${nodeCount}</strong>`));
    this.infoBarElement.appendChild(createInfoBarText(`Edges: <strong>${edgeCount}</strong>`));
    this.infoBarElement.appendChild(createInfoBarText(`State: <strong>${currentState}</strong>`));
    this.infoBarElement.appendChild(createInfoBarText(`Energy: <strong>${totalEnergy}</strong>`));
    
    // Update header title with node count
    const titleElement = this.headerElement.querySelector('.header-title');
    if (titleElement) {
      titleElement.textContent = `🧠 Neural Network Simulator (${nodeCount} nodes)`;
    }
  }
  
  // Action methods
  private exportData() {
    try {
      const state = this.network.exportState();
      const blob = new Blob([state], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `neuropsymap-export-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      this.showNotification('Data exported successfully!');
    } catch (error) {
      console.error('[Export] Failed:', error);
      this.showNotification('Export failed');
    }
  }
  
  private loadData() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.setAttribute('aria-label', 'Load neural network state from file');
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const content = event.target?.result as string;
            this.network.importState(content);
            this.showNotification('State loaded successfully!');
            this.update();
          } catch (error) {
            console.error('[Import] Failed:', error);
            this.showNotification('Failed to load state');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  }
  
  private toggleSun() {
    this.network.toggleSun();
    this.showNotification(
      this.network.sunEnabled 
        ? '☀️ Sun energy enabled!' 
        : '☀️ Sun energy disabled!'
    );
  }
  
  private cyclePersonality() {
    const personalities = ['warm', 'cold', 'energetic', 'calm', 'mysterious', 'playful', 'wise', 'youthful'];
    const currentIndex = personalities.indexOf(this.network.voiceCharacteristics.personality);
    const newIndex = (currentIndex + 1) % personalities.length;
    this.network.setVoicePersonality(personalities[newIndex] as any);
    this.showNotification(`Personality: ${personalities[newIndex]}`);
    this.update();
  }
  
  private cycleEmotion() {
    const emotions = ['neutral', 'happy', 'sad', 'excited', 'contemplative', 'curious', 'confident', 'uncertain'];
    const currentIndex = emotions.indexOf(this.network.voiceCharacteristics.emotionalState);
    const newIndex = (currentIndex + 1) % emotions.length;
    this.network.setEmotionalState(emotions[newIndex] as any);
    this.showNotification(`Emotion: ${emotions[newIndex]}`);
    this.update();
  }
  
  private toggleBreathingSounds() {
    // Just toggle the breathing sounds setting without checking current state
    // We'll assume it's toggled and let the system handle the state
    this.network.breathingSystem.setBreathingSoundEnabled(true);
    this.showNotification('🔊 Breathing sounds toggled');
  }
  
  private showNotification(message: string, duration: number = 2500) {
    const notif = document.createElement('div');
    notif.style.position = 'fixed';
    notif.style.bottom = '32px';
    notif.style.right = '32px';
    notif.style.background = 'var(--color-bg-secondary)';
    notif.style.color = 'var(--color-text-primary)';
    notif.style.padding = 'var(--space-3) var(--space-4)';
    notif.style.borderRadius = 'var(--radius-md)';
    notif.style.boxShadow = 'var(--shadow-lg)';
    notif.style.zIndex = '10000';
    notif.style.fontSize = 'var(--font-size-base)';
    notif.textContent = message;
    notif.className = 'card';
    document.body.appendChild(notif);
    
    setTimeout(() => {
      notif.style.opacity = '0';
      notif.style.transition = 'opacity 0.3s ease';
      setTimeout(() => notif.remove(), 300);
    }, duration);
  }
}

